/**
 * Adapter + agrégateur form-polls — pure functions, sans I/O.
 *
 * Sprint 2 form-polls launch (mai 2026, B7) : fait suite à la migration
 * `addFormResponse → VoteService.castVote`. Permet aux composants
 * `FormPollResults` / `FormPollVote` de consommer `VoteService.listByPollIdAndType`
 * et d'agréger les résultats côté client, sans dépendre de localStorage.
 *
 * - `voteRowToFormResponse` : adapte `VoteRow` (shape Supabase) → `FormResponse`
 *   (shape historique attendue par les composants existants).
 * - `aggregateFormResults` : agrégation client-side extraite de l'ancien
 *   `pollStorage.getFormResults` — pure function, déterministe, testable.
 */

import type { VoteRow } from "../services/VoteService";
import type {
  FormResponse,
  FormResponseItem,
  FormResults,
  FormQuestionShape,
  DateQuestionResults,
} from "./poll";

/**
 * Convertit un `VoteRow` (form) en `FormResponse` legacy.
 *
 * Le `responseData` form a la shape `{ responses: FormResponseItem[], email?: string }`
 * (cf. VoteService.ts §responseData). On déballe vers la structure attendue
 * par les composants (items au top-level, deviceId via stableFingerprint).
 */
export function voteRowToFormResponse(row: VoteRow): FormResponse {
  const data = row.responseData ?? {};
  const items = Array.isArray((data as any).responses)
    ? ((data as any).responses as FormResponseItem[])
    : [];
  const respondentEmail =
    typeof (data as any).email === "string"
      ? ((data as any).email as string)
      : undefined;
  // Fallback voterName : la RPC get_poll_responses stripe respondent_name
  // (PII) mais laisse passer response_data — le nom y est dupliqué au vote.
  const voterName =
    typeof (data as any).voterName === "string" &&
    (data as any).voterName.trim()
      ? ((data as any).voterName as string).trim()
      : undefined;

  return {
    id: row.id,
    pollId: row.pollId,
    respondentName: row.respondentName || voterName,
    respondentEmail,
    items,
    deviceId: row.stableFingerprint,
    created_at: row.createdAt,
  };
}

/**
 * Agrège une liste de `FormResponse` en `FormResults` (counts par question,
 * échantillons texte). Pure function — extraite de l'ancien
 * `pollStorage.getFormResults` mais sans `getFormPollById` (pas d'I/O,
 * les questions sont passées en paramètre).
 */
export function aggregateFormResults(
  pollId: string,
  responses: FormResponse[],
  questions: FormQuestionShape[],
): FormResults {
  const countsByQuestion: Record<string, Record<string, number>> = {};
  const textAnswers: Record<string, string[]> = {};
  const dateResults: Record<string, DateQuestionResults> = {};

  questions.forEach((q) => {
    countsByQuestion[q.id] = {};
    textAnswers[q.id] = [];
  });

  responses.forEach((resp) => {
    resp.items.forEach((item) => {
      const q = questions.find((quest) => quest.id === item.questionId);
      if (!q) return;

      const kind = q.kind as string;
      if (kind === "text" || kind === "long-text") {
        if (item.value) textAnswers[q.id].push(String(item.value));
      } else if (kind === "single" || kind === "rating" || kind === "nps") {
        // B-data fix : "nps" agrégé comme "rating" (count par valeur numérique).
        // Était ignoré → NPS non remonté dans les résultats (test 31/05).
        const val = String(item.value);
        if (!countsByQuestion[q.id]) countsByQuestion[q.id] = {};
        countsByQuestion[q.id][val] = (countsByQuestion[q.id][val] || 0) + 1;
      } else if (kind === "multiple") {
        const vals = Array.isArray(item.value) ? item.value : [item.value];
        vals.forEach((v) => {
          const val = String(v);
          if (!countsByQuestion[q.id]) countsByQuestion[q.id] = {};
          countsByQuestion[q.id][val] = (countsByQuestion[q.id][val] || 0) + 1;
        });
      } else if (kind === "matrix") {
        // B-data fix : matrice. item.value = { [rowId]: colId | colId[] }.
        // Clé agrégée "rowId::colId" → countsByQuestion. Était ignoré → 0.
        const rows =
          item.value && typeof item.value === "object"
            ? (item.value as Record<string, unknown>)
            : {};
        if (!countsByQuestion[q.id]) countsByQuestion[q.id] = {};
        for (const [rowId, colVal] of Object.entries(rows)) {
          const cols = Array.isArray(colVal) ? colVal : [colVal];
          for (const c of cols) {
            if (!c) continue;
            // Clé `rowId_colId` (séparateur "_") — DOIT matcher le render
            // FormPollResults.tsx : `${row.id}_${col.id}`. Avant "::" → 0%.
            const key = `${rowId}_${String(c)}`;
            countsByQuestion[q.id][key] =
              (countsByQuestion[q.id][key] || 0) + 1;
          }
        }
      }
    });
  });

  return {
    pollId,
    countsByQuestion,
    textAnswers,
    dateResults,
    totalResponses: responses.length,
  };
}
